import AbortError from '@errors/AbortError'
import BadRequestError from '@errors/BadRequestError'
import HttpError from '@errors/HttpError'
import SignedOutError from '@errors/SignedOutError'

export interface ApiErrorHandlerOptions<TBad = unknown> {
  showError: (e: { title: string; reason: unknown }) => void
  // システムエラー時の基本文言（例: '保存できませんでした。'）。status は自動で末尾に付与する。
  systemMessage: string
  // 通信エラー時の文言。既定は 'ネットワークを確認してください。'。
  networkMessage?: string
  // 400 の本文を受け取って処理するコールバック。指定時は 400 をここで処理しシステムエラーは出さない。
  // 未指定なら 400 も HttpError 分岐でシステムエラー(400) になる（onBadRequest 無し画面の挙動）。
  onBadRequest?: (body: TBad) => void
  // 成功/各エラーで実行したい後始末。abort/401 では実行しない（中断後の誤発火を防ぐ）。
  onFinally?: () => void
}

// jsonApi(...).then(onSuccess, apiErrorHandler({...})) の形でエラー側の定型分類を共通化する。
// - abort / 401(SignedOut) は無表示・onFinally もスキップ（shim の skip-on-401 セマンティクスを踏襲）
// - 400(BadRequest) は onBadRequest 指定時はそれに委譲、未指定時は HttpError 分岐でシステムエラー(400)
// - その他 HttpError → システムエラー(${status}) / それ以外 → 通信エラー
const apiErrorHandler =
  <TBad = unknown>(options: ApiErrorHandlerOptions<TBad>) =>
  (error: unknown): void => {
    if (error instanceof AbortError || error instanceof SignedOutError) return
    if (error instanceof BadRequestError && options.onBadRequest) {
      options.onBadRequest(error.body as TBad)
      options.onFinally?.()
      return
    }
    if (error instanceof HttpError) {
      options.showError({
        title: 'システムエラー',
        reason: `${options.systemMessage}(${error.status})`,
      })
    } else {
      options.showError({
        title: '通信エラー',
        reason: options.networkMessage ?? 'ネットワークを確認してください。',
      })
    }
    options.onFinally?.()
  }

export { apiErrorHandler }
export default apiErrorHandler
